<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');

const query = ref('');
const isLoading = computed(
  () => uiFlags.value?.isFetching || uiFlags.value?.isSearching
);

const loadContacts = () => {
  store.dispatch('contacts/get', { page: 1, sortAttr: 'last_activity_at' });
};

const searchContacts = debounce(value => {
  if (value.trim()) {
    store.dispatch('contacts/search', {
      search: encodeURIComponent(value.trim()),
      page: 1,
    });
  } else {
    loadContacts();
  }
}, 300);

watch(query, value => searchContacts(value));

onMounted(() => loadContacts());
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col gap-3">
    <Input
      v-model="query"
      type="search"
      size="sm"
      :placeholder="t('TASKS.FORM.SEARCH_CONTACT')"
      :custom-input-class="['ltr:!pl-8 rtl:!pr-8']"
    >
      <template #prefix>
        <Icon
          icon="i-lucide-search"
          class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
        />
      </template>
    </Input>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <div v-if="isLoading" class="flex items-center justify-center py-10">
        <Spinner />
      </div>

      <template v-else-if="contacts.length">
        <button
          v-for="contact in contacts"
          :key="contact.id"
          type="button"
          class="flex w-full items-center gap-3 border-b border-n-weak px-1 py-3 text-left last:border-b-0 hover:bg-n-alpha-1"
          :class="{ 'bg-n-brand/5': String(modelValue) === String(contact.id) }"
          @click="emit('update:modelValue', contact.id)"
        >
          <Avatar
            :name="contact.name"
            :src="contact.thumbnail"
            :size="36"
            rounded-full
            class="shrink-0"
          />
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ contact.name }}
            </span>
            <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span
                v-if="contact.email"
                class="truncate text-xs text-n-slate-11"
              >
                {{ contact.email }}
              </span>
              <span
                v-if="contact.email && contact.phoneNumber"
                class="h-3 w-px shrink-0 rounded bg-n-slate-6"
              />
              <span
                v-if="contact.phoneNumber"
                class="truncate text-xs text-n-slate-11"
              >
                {{ contact.phoneNumber }}
              </span>
            </span>
          </span>
          <span
            v-if="String(modelValue) === String(contact.id)"
            class="i-lucide-check size-4 shrink-0 text-n-brand"
          />
        </button>
      </template>

      <p v-else class="px-3 py-8 text-center text-sm text-n-slate-11">
        {{ t('TASKS.FORM.NO_CONTACTS') }}
      </p>
    </div>
  </div>
</template>
